import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import SearchInput from './components/SearchInput';
import UserResultCard from './components/UserResultCard';
import SearchFilters from './components/SearchFilters';
import EmptyState from './components/EmptyState';
import LoadingState from './components/LoadingState';
import PaginationControls from './components/PaginationControls';
import { useAuth } from '../../contexts/AuthContext';
import { friendService } from '../../lib/friendService';

const SearchResults = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState(searchParams?.get('q') || '');
  const [searchResults, setSearchResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState({});
  const [hasSearched, setHasSearched] = useState(false);

  const { user: authUser, signOut } = useAuth();
  
  const currentUser = authUser ? {
    id: authUser.id,
    name: authUser.user_metadata?.full_name || authUser.email,
    avatar: authUser.user_metadata?.avatar_url || 'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=150&h=150&fit=crop&crop=face',
    username: authUser.user_metadata?.username || authUser.email.split('@')[0]
  } : null;

  const resultsPerPage = 10;

  useEffect(() => {
    const query = searchParams?.get('q');
    if (query) {
      setSearchQuery(query);
      performSearch(query);
    }
  }, [searchParams]);

  const performSearch = async (query) => {
    if (!query?.trim()) return;

    setIsLoading(true);
    setHasSearched(true);

    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .ilike('full_name', `%${query}%`)
        .limit(20);

      if (error) throw error;

      // Filter out the current user
      const otherUsers = data.filter(u => u.id !== currentUser?.id);

      const formattedResults = otherUsers.map(profile => ({
        id: profile.id,
        name: profile.full_name,
        avatar: profile.avatar_url || "https://images.unsplash.com/photo-1507532459814-b32f63cf4497",
        location: profile.location || "N/A",
        friendshipStatus: "none", 
      }));

      setSearchResults(formattedResults);
      setCurrentPage(1);
    } catch (error) {
      console.error('Search failed:', error);
      setSearchResults([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
    setSearchParams({ q: query });
    performSearch(query);
  };

  const handleQueryChange = (query) => {
    setSearchQuery(query);
    if (!query?.trim()) {
      setSearchParams({});
      setSearchResults([]);
      setHasSearched(false);
    }
  };

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
    if (searchQuery?.trim()) {
      performSearch(searchQuery);
    }
  };

  const handleAddFriend = async (userId) => {
    try {
      if (!currentUser) return;
      await friendService.sendFriendRequest(currentUser.id, userId);
      setSearchResults(prev => prev.map(user => 
        user.id === userId ? { ...user, friendshipStatus: 'pending' } : user
      ));
    } catch (error) {
      console.error('Failed to send friend request:', error);
    }
  };

  const handleMessage = (userId) => {
    navigate(`/messages/${userId}`);
  };

  const handleViewProfile = (userId) => {
    navigate(`/user-profile?id=${userId}`);
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setSearchParams({});
    setSearchResults([]);
    setHasSearched(false);
    setCurrentPage(1);
  };

  const handleRefineSearch = (newQuery = '') => {
    if (newQuery) {
      handleSearch(newQuery);
    } else {
      // Focus on search input
      document.querySelector('input[type="search"]')?.focus();
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Calculate pagination
  const totalResults = searchResults?.length;
  const totalPages = Math.ceil(totalResults / resultsPerPage);
  const startIndex = (currentPage - 1) * resultsPerPage;
  const endIndex = startIndex + resultsPerPage;
  const currentResults = searchResults?.slice(startIndex, endIndex);

  return (
    <>
      <Helmet>
        <title>Artist Directory - Aether</title>
        <meta name="description" content="Find and connect with elite 3D artists, VFX specialists, and engineers on Aether." />
      </Helmet>
      <div className="min-h-screen">
        <Header
          user={currentUser}
          onLogout={() => navigate('/login')}
          notificationCount={3} />


        <main className="pt-20 pb-8">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Search Header */}
            <div className="mb-8">
              <h1 className="text-2xl font-bold text-primary mb-4 font-playfair tracking-wide">
                Artist Directory
              </h1>
              <SearchInput
                initialQuery={searchQuery}
                onSearch={handleSearch}
                onQueryChange={handleQueryChange} />

            </div>

            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
              {/* Filters Sidebar */}
              <div className="lg:col-span-1">
                <SearchFilters
                  onFiltersChange={handleFiltersChange}
                  activeFilters={filters} />

              </div>

              {/* Results Content */}
              <div className="lg:col-span-3">
                {isLoading ?
                <LoadingState count={6} /> :
                hasSearched ?
                searchResults?.length > 0 ?
                <>
                      {/* Results Header */}
                      <div className="flex items-center justify-between mb-6">
                        <h2 className="text-lg font-semibold text-foreground">
                          Search Results ({totalResults})
                        </h2>
                        {searchQuery &&
                    <p className="text-sm text-muted-foreground">
                            Results for "{searchQuery}"
                          </p>
                    }
                      </div>

                      {/* Results List */}
                      <div className="space-y-4">
                        {currentResults?.map((user) =>
                    <UserResultCard
                      key={user?.id}
                      user={user}
                      onAddFriend={handleAddFriend}
                      onMessage={handleMessage}
                      onViewProfile={handleViewProfile} />

                    )}
                      </div>

                      {/* Pagination */}
                      <PaginationControls
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalResults={totalResults}
                    resultsPerPage={resultsPerPage}
                    onPageChange={handlePageChange}
                    isLoading={isLoading} />

                    </> :

                <EmptyState
                  query={searchQuery}
                  onClearSearch={handleClearSearch}
                  onRefineSearch={handleRefineSearch} /> :



                <EmptyState
                  query=""
                  onClearSearch={handleClearSearch}
                  onRefineSearch={handleRefineSearch} />

                }
              </div>
            </div>
          </div>
        </main>
      </div>
    </>);

};

export default SearchResults;